import React, { useState, useEffect } from 'react';
import {
  Table, Tag, Tooltip, Card, Row, Col, Statistic, Badge, Spin, Alert, Empty, Button, Select, Input,
  Tabs, Typography, Space, Avatar, Divider
} from 'antd';
import {
  ShopOutlined, CheckCircleOutlined, CloseCircleOutlined, UserOutlined,
  DollarOutlined, CalendarOutlined, SearchOutlined, FilterOutlined, ReloadOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import 'antd/dist/reset.css';
import Chart from 'react-apexcharts';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const OrdersPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');

  // Metrics
  const [metrics, setMetrics] = useState({
    totalOrders: 0,
    activeSubscriptions: 0,
    totalRevenue: 0,
    yearlyPlans: 0,
    addonPlans: 0,
  });

  // Chart states
  const [ordersByPlanType, setOrdersByPlanType] = useState({
    labels: ['Yearly', 'Addon'],
    series: [0, 0]
  });

  const [revenueByMonth, setRevenueByMonth] = useState({
    categories: [],
    series: [{
      name: 'Revenue',
      data: []
    }]
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    if (orders.length > 0) {
      applyFilters();
      calculateMetrics();
      prepareChartData();
    }
  }, [orders, searchText, filterStatus, filterPlan]);

  // Add a utility function for retrying API calls
  const fetchWithRetry = async (url, options = {}, retries = 2, delay = 1000) => {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (retries <= 0) {
        throw error;
      }
      console.log(`Retrying fetch to ${url}. Attempts remaining: ${retries}`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, retries - 1, delay);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await fetchWithRetry('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllOrders', {
        credentials: 'include'
      });
      
      if (data.success && Array.isArray(data.data)) {
        // Process orders
        const processedOrders = processOrdersData(data.data);
        setOrders(processedOrders);
        setFilteredOrders(processedOrders);
      } else if (data.data && Array.isArray(data.data)) {
        // Handle case where success flag might be missing but data is present
        const processedOrders = processOrdersData(data.data);
        setOrders(processedOrders);
        setFilteredOrders(processedOrders);
      } else if (Array.isArray(data)) {
        // Handle case where the API directly returns an array
        const processedOrders = processOrdersData(data);
        setOrders(processedOrders);
        setFilteredOrders(processedOrders);
      } else {
        console.error('Invalid orders data format:', data);
        setOrders([]);
        setFilteredOrders([]);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.message);
      setOrders([]);
      setFilteredOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const processOrdersData = (usersData) => {
    let allOrders = [];
    
    usersData.forEach(userData => {
      const { user, orders } = userData;
      
      if (orders && orders.length > 0) {
        orders.forEach(order => {
          allOrders.push({
            ...order,
            user_name: user.name,
            user_email: user.email,
            user_uid: user.uid,
            key: `${order.id}-${user.uid}`
          });
        });
      }
    });
    
    return allOrders;
  };

  const calculateMetrics = () => {
    if (orders.length === 0) return;

    const totalOrders = orders.length;
    const activeSubscriptions = orders.filter(order => order.status === 'active').length;
    const totalRevenue = orders.reduce((sum, order) => sum + order.total_price, 0);
    const yearlyPlans = orders.filter(order => order.plan_name === 'Yearly').length;
    const addonPlans = orders.filter(order => order.plan_name === 'Addon').length;

    setMetrics({
      totalOrders,
      activeSubscriptions,
      totalRevenue,
      yearlyPlans,
      addonPlans
    });
  };

  const prepareChartData = () => {
    if (orders.length === 0) return;

    // Orders by plan type
    const yearlyPlans = orders.filter(order => order.plan_name === 'Yearly').length;
    const addonPlans = orders.filter(order => order.plan_name === 'Addon').length;
    
    setOrdersByPlanType({
      labels: ['Yearly', 'Addon'],
      series: [yearlyPlans, addonPlans]
    });

    // Revenue by month
    const monthlyRevenue = {};
    const now = new Date();
    const sixMonthsAgo = new Date(now);
    sixMonthsAgo.setMonth(now.getMonth() - 6);

    // Initialize last 6 months
    for (let i = 0; i < 6; i++) {
      const d = new Date(sixMonthsAgo);
      d.setMonth(sixMonthsAgo.getMonth() + i);
      const monthYear = `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
      monthlyRevenue[monthYear] = 0;
    }

    // Add revenue data
    orders.forEach(order => {
      const orderDate = new Date(order.created_at);
      if (orderDate >= sixMonthsAgo) {
        const monthYear = `${orderDate.toLocaleString('default', { month: 'short' })} ${orderDate.getFullYear()}`;
        if (monthlyRevenue[monthYear] !== undefined) {
          monthlyRevenue[monthYear] += order.total_price;
        }
      }
    });

    setRevenueByMonth({
      categories: Object.keys(monthlyRevenue),
      series: [{
        name: 'Revenue',
        data: Object.values(monthlyRevenue)
      }]
    });
  };

  const applyFilters = () => {
    let result = [...orders];
    
    // Apply search filter
    if (searchText) {
      const lowerSearchText = searchText.toLowerCase();
      result = result.filter(order => 
        order.user_name.toLowerCase().includes(lowerSearchText) ||
        order.user_email.toLowerCase().includes(lowerSearchText) ||
        order.plan_name.toLowerCase().includes(lowerSearchText)
      );
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      result = result.filter(order => order.status === filterStatus);
    }
    
    // Apply plan filter
    if (filterPlan !== 'all') {
      result = result.filter(order => order.plan_name === filterPlan);
    }
    
    setFilteredOrders(result);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleStatusFilterChange = (value) => {
    setFilterStatus(value);
  };

  const handlePlanFilterChange = (value) => {
    setFilterPlan(value);
  };

  const resetFilters = () => {
    setSearchText('');
    setFilterStatus('all');
    setFilterPlan('all');
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'User',
      dataIndex: 'user_name',
      key: 'user',
      width: 220,
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar icon={<UserOutlined />} style={{ backgroundColor: 'var(--primary-color)' }} />
          <div>
            <div style={{ fontWeight: '500' }}>{text || 'Unknown'}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{record.user_email || 'No email'}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Subscription',
      key: 'subscription',
      width: 150,
      render: (_, record) => (
        <div>
          <Tag 
            color={record.plan_name === 'Yearly' ? 'blue' : 'green'}
            style={{ fontWeight: '500', padding: '2px 10px' }}
          >
            {record.plan_name}
          </Tag>
          <div style={{ marginTop: '4px' }}>
            <Badge
              status={record.status === 'active' ? 'success' : 'error'}
              text={<span style={{ textTransform: 'capitalize' }}>{record.status}</span>}
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 100,
      render: (price) => (
        <Text strong style={{ color: 'var(--secondary-color)' }}>
          ${price.toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Coins Added',
      dataIndex: 'coins_added',
      key: 'coins_added',
      width: 110,
      render: (coins) => (
        <div style={{ fontWeight: '500' }}>
          {coins} <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>coins</span>
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (date) => {
        const formattedDate = new Date(date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        const formattedTime = new Date(date).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        });
        return (
          <div>
            <div>{formattedDate}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{formattedTime}</div>
          </div>
        );
      },
    },
    {
      title: 'Valid Until',
      dataIndex: 'plan_valid_till',
      key: 'plan_valid_till',
      width: 170,
      render: (date) => {
        if (!date) return <Text type="secondary">N/A</Text>;
        
        const validDate = new Date(date);
        const now = new Date();
        const isExpired = validDate < now;
        
        const daysLeft = Math.ceil((validDate - now) / (1000 * 60 * 60 * 24));
        
        const formattedDate = validDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        
        return (
          <div>
            <div style={{ 
              color: isExpired ? 'var(--danger-color)' : 'var(--text-primary)',
              fontWeight: isExpired ? '500' : 'normal'
            }}>
              {formattedDate}
            </div>
            {!isExpired ? (
              <Tag color="green" style={{ marginTop: '4px', fontSize: '11px' }}>
                {daysLeft} days left
              </Tag>
            ) : (
              <Tag color="red" style={{ marginTop: '4px', fontSize: '11px' }}>
                Expired
              </Tag>
            )}
          </div>
        );
      },
    },
  ];

  // Chart options
  const donutOptions = {
    chart: {
      type: 'donut',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    legend: {
      position: 'bottom',
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--primary-color)', 'var(--secondary-color)'],
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          height: 250,
        },
        legend: {
          show: false
        }
      }
    }],
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true,
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: 600,
              formatter: function (val) {
                return val;
              }
            },
            total: {
              show: true,
              label: 'Total',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              }
            }
          }
        }
      }
    }
  };

  const barOptions = {
    chart: {
      type: 'bar',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: '60%'
      },
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--primary-color)'],
    xaxis: {
      categories: revenueByMonth.categories,
      labels: {
        style: {
          fontSize: '12px',
          colors: 'var(--text-tertiary)'
        }
      }
    },
    yaxis: {
      title: {
        text: 'Revenue ($)',
        style: {
          fontSize: '14px',
          color: 'var(--text-secondary)'
        }
      },
      labels: {
        formatter: (value) => { return '$' + value.toFixed(0) }
      }
    },
    fill: {
      opacity: 1,
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: "vertical",
        shadeIntensity: 0.25,
        gradientToColors: undefined,
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 0.85,
        stops: [50, 100],
      },
    },
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '2rem' }}>
        <Spin size="large" tip="Loading subscription data..." spinning={true} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Data"
        description={error}
        type="error"
        showIcon
        style={{ margin: '2rem' }}
      />
    );
  }

  return (
    <div style={{ padding: '24px', background: 'var(--background-primary)', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={3} style={{ color: 'var(--primary-color)', margin: 0 }}>
          <ShopOutlined /> Subscription Management
        </Title>
        <Text type="secondary">
          Overview and management of all user subscription plans
        </Text>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card primary">
            <div>
              <div className="stat-value">{metrics.totalOrders}</div>
              <div className="stat-label">Total Subscriptions</div>
            </div>
            <ShopOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card secondary">
            <div>
              <div className="stat-value">{metrics.activeSubscriptions}</div>
              <div className="stat-label">Active Subscriptions</div>
            </div>
            <CheckCircleOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card accent">
            <div>
              <div className="stat-value">${metrics.totalRevenue.toLocaleString()}</div>
              <div className="stat-label">Total Revenue</div>
            </div>
            <DollarOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card warning">
            <div>
              <div className="stat-value">{((metrics.activeSubscriptions / metrics.totalOrders) * 100).toFixed(0)}%</div>
              <div className="stat-label">Active Rate</div>
            </div>
            <BarChartOutlined className="stat-icon" />
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card 
            title="Revenue Trend (Last 6 Months)" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            {revenueByMonth.series[0].data.length > 0 ? (
              <Chart
                options={{
                  ...barOptions,
                  xaxis: {
                    ...barOptions.xaxis,
                    categories: revenueByMonth.categories
                  }
                }}
                series={revenueByMonth.series}
                type="bar"
                height="280"
              />
            ) : (
              <Empty description="No revenue data available" style={{ marginTop: '80px' }} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="Subscription Plan Distribution" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            {ordersByPlanType.series.some(value => value > 0) ? (
              <Chart
                options={{
                  ...donutOptions,
                  labels: ordersByPlanType.labels
                }}
                series={ordersByPlanType.series}
                type="donut"
                height="280"
              />
            ) : (
              <Empty description="No subscription data available" style={{ marginTop: '80px' }} />
            )}
          </Card>
        </Col>
      </Row>

      <Card className="dashboard-card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', marginBottom: '16px', gap: '16px', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search by name, email or plan"
            value={searchText}
            onChange={handleSearch}
            style={{ maxWidth: '300px' }}
            prefix={<SearchOutlined />}
            allowClear
          />
          
          <Space wrap>
            <Select
              value={filterStatus}
              onChange={handleStatusFilterChange}
              style={{ width: '160px' }}
              placeholder="Filter by status"
            >
              <Option value="all">All Statuses</Option>
              <Option value="active">Active</Option>
              <Option value="expired">Expired</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
            
            <Select
              value={filterPlan}
              onChange={handlePlanFilterChange}
              style={{ width: '160px' }}
              placeholder="Filter by plan"
            >
              <Option value="all">All Plans</Option>
              <Option value="Yearly">Yearly</Option>
              <Option value="Addon">Addon</Option>
            </Select>
            
            <Button 
              icon={<ReloadOutlined />} 
              onClick={resetFilters}
              type="text"
              title="Reset filters"
            >
              Reset
            </Button>
          </Space>
          
          <div style={{ marginLeft: 'auto' }}>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />}
              onClick={fetchOrders}
            >
              Refresh
            </Button>
          </div>
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredOrders}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} subscriptions`
          }}
          scroll={{ x: 1000 }}
          sticky
          locale={{
            emptyText: (
              <Empty description="No subscription records found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )
          }}
        />
      </Card>
    </div>
  );
};

export default OrdersPage;